import { CoachingFormAnswerType, ICoachingForm } from '@/js/services/api/coachingForm/types';
import {
    CoachingType,
    IProtocol,
    IProtocolAnswer,
    RequestPatchProtocol,
    RequestPostProtocol,
    RequestPostProtocolWithoutInteraction,
} from '@/js/services/api/protocol/protocolModel';
import { ProtocolFormState, ProtocolFormStateQuestion } from './coachingForm.types';
import { ICoachingFormReasonCode } from '@/js/services/api/coachingForm/types';
import { ProtocolFormStateReasonCode } from './coachingForm.types';
import { transformReasonCodesToNestedReasonCodes } from '@/js/services/api/coachingForm/utils';

function mapReasonCodesToProtocolState(
    codes: ICoachingFormReasonCode[]
): ProtocolFormStateReasonCode[] {
    return transformReasonCodesToNestedReasonCodes(codes).map((code) => ({
        id: code.id,
        code: code.code,
        childCodes: code.childCodes?.map((child) => ({
            id: child.id,
            code: child.code,
        })),
    }));
}

export function calculateCoachingFormQuestionPoints(question: ProtocolFormStateQuestion): {
    points: number;
    maxPoints: number;
    isNotApplicable: boolean;
} {
    const result = { points: 0, maxPoints: 0, isNotApplicable: false };

    if (
        question.answerType === CoachingFormAnswerType.Affirmative ||
        question.answerType === CoachingFormAnswerType.Custom ||
        question.answerType === CoachingFormAnswerType.Interval
    ) {
        const selectedAnswer = question.selectedOptionId
            ? question.answers?.find((answer) => answer.id === question.selectedOptionId)
            : undefined;

        if (selectedAnswer?.notApplicable) {
            return { points: 0, maxPoints: 0, isNotApplicable: true };
        }

        result.points = selectedAnswer?.points ?? 0;
        result.maxPoints = Math.max(...(question.answers?.map((answer) => answer.points) ?? []), 0);
    }

    return result;
}

export function calculateSectionPoints(protocol: ProtocolFormState) {
    let pointMap: { [key: string]: { points: number; maxPoints: number } } = {};
    protocol.sections.forEach((section) => {
        pointMap[section.id] = section.questions.reduce(
            (acc, question) => {
                const questionPoints = calculateCoachingFormQuestionPoints(question);
                return {
                    points: acc.points + questionPoints.points,
                    maxPoints: acc.maxPoints + questionPoints.maxPoints,
                };
            },
            {
                points: 0,
                maxPoints: 0,
            }
        );
    });
    pointMap.total = Object.values(pointMap).reduce(
        (acc, sectionPoints) => {
            return {
                points: acc.points + sectionPoints.points,
                maxPoints: acc.maxPoints + sectionPoints.maxPoints,
            };
        },
        { points: 0, maxPoints: 0 }
    );
    return pointMap;
}

export function generatePostProtocolWithoutInteractionRequest(
    coachingFormState: ProtocolFormState,
    agentId: string,
    manualInteractionReference: string | undefined,
    formId: string
): RequestPostProtocolWithoutInteraction {
    const selectedReasonCodeIds = [
        coachingFormState.reasonCodes?.selectedParentId,
        coachingFormState.reasonCodes?.selectedChildId,
    ].filter((id): id is string => !!id);
    return {
        coachingType: coachingFormState.coachingType,
        manualInteractionReference: manualInteractionReference,
        agentId: agentId,
        channelId: coachingFormState.channelId!,
        openComment: coachingFormState.openComment?.comment,
        restrictedComment: coachingFormState.restrictedComment?.comment,
        formDefinitionId: formId,
        actionText: coachingFormState.action?.text,
        actionCompleted: coachingFormState.action?.completed,
        selectedReasonCodeIds,
        sections: coachingFormState.sections.map((section) => ({
            formSectionId: section.id,
            comment: section.comment,
            answers: section.questions.reduce((acc, question) => {
                const answer = generateProtocolSectionAnswer(question);
                if (answer) {
                    acc.push(answer);
                }
                return acc;
            }, [] as IProtocolAnswer[]),
        })),
    };
}

export function generatePostProtocolRequest(
    coachingFormState: ProtocolFormState,
    interactionId: string,
    formId: string
): RequestPostProtocol {
    const selectedReasonCodeIds = [
        coachingFormState.reasonCodes?.selectedParentId,
        coachingFormState.reasonCodes?.selectedChildId,
    ].filter((id): id is string => !!id);
    return {
        coachingType: coachingFormState.coachingType,
        interactionPartId: interactionId,
        openComment: coachingFormState.openComment?.comment,
        restrictedComment: coachingFormState.restrictedComment?.comment,
        formDefinitionId: formId,
        actionText: coachingFormState.action?.text,
        actionCompleted: coachingFormState.action?.completed,
        selectedReasonCodeIds,
        sections: coachingFormState.sections.map((section) => ({
            formSectionId: section.id,
            comment: section.comment,
            comments: section.comments,
            answers: section.questions.reduce((acc, question) => {
                const answer = generateProtocolSectionAnswer(question);
                if (answer) {
                    acc.push(answer);
                }
                return acc;
            }, [] as IProtocolAnswer[]),
        })),
    };
}

export function generatePatchProtocolRequest(protocol: ProtocolFormState): RequestPatchProtocol {
    const selectedReasonCodeIds = [
        protocol.reasonCodes?.selectedParentId,
        protocol.reasonCodes?.selectedChildId,
    ].filter((id): id is string => !!id);

    return {
        openComment: protocol.openComment?.comment,
        restrictedComment: protocol.restrictedComment?.comment,
        coachingType: protocol.coachingType,
        actionText: protocol.action?.text,
        actionCompleted: protocol.action?.completed,
        selectedReasonCodeIds: protocol.reasonCodes ? selectedReasonCodeIds : undefined,
        sections: protocol.sections.map((section) => ({
            formSectionId: section.id,
            comment: section.comment,
            answers: section.questions.map((question) => ({
                formQuestionId: question.id,
                selectedOptionId: question.selectedOptionId,
                comment: question.comment || undefined,
                freeTextAnswer: question.textValue || undefined,
            })),
        })),
    };
}

export function generateProtocolSectionAnswer<
    T extends CoachingFormAnswerType = CoachingFormAnswerType,
>(question: ProtocolFormStateQuestion<T>): IProtocolAnswer | undefined {
    if (question.answerType === CoachingFormAnswerType.Text && question.textValue) {
        return {
            comment: question.comment,
            comments: question.comments,
            formQuestionId: question.id,
            answerType: question.answerType,
            freeTextAnswer: question.textValue,
            protocolQuestionAnswerId: question.protocolQuestionAnswerId,
        };
    } else if (
        (question.answerType === CoachingFormAnswerType.Affirmative ||
            question.answerType === CoachingFormAnswerType.Custom ||
            question.answerType === CoachingFormAnswerType.Interval) &&
        question.selectedOptionId
    ) {
        return {
            comment: question.comment,
            comments: question.comments,
            formQuestionId: question.id,
            answerType: question.answerType,
            selectedOptionId: question.selectedOptionId,
            protocolQuestionAnswerId: question.protocolQuestionAnswerId,
        };
    }
    return;
}

export function createProtocolState(
    form: ICoachingForm,
    protocolData: IProtocol
): ProtocolFormState {
    const selectedIds = protocolData.selectedReasonCodeIds || [];
    const allReasonCodes = form.reasonCodes?.codes || [];

    const selectedParentFromIds = allReasonCodes.find(
        (code) => selectedIds.includes(code.id) && !code.parentId
    );

    const selectedChildFromIds = allReasonCodes.find(
        (code) => selectedIds.includes(code.id) && !!code.parentId
    );
    const protocol: ProtocolFormState = {
        protocolId: protocolData.id,
        coachingType: protocolData.coachingType,
        action: form.useAction
            ? {
                  description: form.action,
                  text: protocolData.actionText || '',
                  completed: protocolData.actionCompleted || false,
              }
            : undefined,
        reasonCodes: form.reasonCodes
            ? {
                  title: form.reasonCodes.title,
                  description: form.reasonCodes.description || '',
                  reasonCodeDescription: form.reasonCodes.reasonCodeDescription || '',
                  threshold: form.reasonCodes.threshold,
                  codes: mapReasonCodesToProtocolState(form.reasonCodes.codes ?? []),
                  selectedParentId: selectedParentFromIds?.id || '',
                  selectedChildId: selectedChildFromIds?.id || '',
              }
            : undefined,
        approved: protocolData.approved,
        requiresApproval: protocolData.requiresApproval,
        isSelfCoached: protocolData.isSelfCoached,
        openComment: form.openComment
            ? {
                  ...form.openComment,
                  comment: protocolData.openComment || '',
              }
            : undefined,
        restrictedComment: form.restrictedComment
            ? {
                  ...form.restrictedComment,
                  comment: protocolData.restrictedComment || '',
              }
            : undefined,
        submittedBy: protocolData.submittedBy,
        createdAt: protocolData.createdAt,
        createdByAi: protocolData.createdByAi,
        editedBy: protocolData.editedBy,
        sections: form.sections.map((formSection) => {
            const protocolSection = protocolData.sections.find(
                (s) => s.formSectionId === formSection.id
            );
            return {
                ...formSection,
                protocolSectionId: protocolSection?.id,
                comment: protocolSection?.comment || '',
                comments: protocolSection?.comments || [],
                questions: formSection.questions.map((formQuestion) => {
                    const protocolQuestion = protocolSection?.answers.find(
                        (a) => a.formQuestionId === formQuestion.id
                    );
                    return {
                        ...formQuestion,
                        comment: protocolQuestion?.comment,
                        comments: protocolQuestion?.comments || [],
                        selectedOptionId: protocolQuestion?.selectedOptionId,
                        textValue: protocolQuestion?.textValue,
                        protocolQuestionAnswerId: protocolQuestion?.protocolQuestionAnswerId,
                    } as ProtocolFormStateQuestion;
                }),
            };
        }),
    };
    return protocol;
}

export function createEmptyProtocolState(form: ICoachingForm): ProtocolFormState {
    const protocol: ProtocolFormState = {
        protocolId: '',
        coachingType: CoachingType.REMOTE,
        channelId: undefined,
        action: form.useAction
            ? {
                  description: form.action,
                  text: '',
                  completed: false,
              }
            : undefined,
        reasonCodes: form.reasonCodes
            ? {
                  title: form.reasonCodes.title,
                  description: form.reasonCodes.description || '',
                  reasonCodeDescription: form.reasonCodes.reasonCodeDescription || '',
                  threshold: form.reasonCodes.threshold,
                  codes: mapReasonCodesToProtocolState(form.reasonCodes.codes ?? []),
                  selectedParentId: '',
                  selectedChildId: '',
              }
            : undefined,
        openComment: form.openComment ? { ...form.openComment, comment: '' } : undefined,
        restrictedComment: form.restrictedComment
            ? { ...form.restrictedComment, comment: '' }
            : undefined,
        sections: form.sections.map((section) => ({
            ...section,
            questions: section.questions.map((question) => ({
                ...question,
                selectedOptionId: '',
            })),
        })),
    };

    return protocol;
}
